import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { ALL_VENUES } from './data';

function VenueCard({ venue, index }: { venue: typeof ALL_VENUES[0]; index: number }) {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <div
      ref={ref}
      className={`bg-white rounded-3xl border border-gray-100 overflow-hidden transition-all duration-700 hover:border-primary/30 hover:-translate-y-1 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
      style={{ transitionDelay: `${index * 120}ms` }}
    >
      <div className="relative h-56 overflow-hidden">
        <img
          src="/img/stib/local-convencoes-int.webp"
          alt={venue.name}
          className="w-full h-full object-cover object-top transition-transform duration-700 hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        <div className="absolute bottom-4 left-4 right-4">
          <span className="inline-block bg-primary/90 text-white text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-2">
            {venue.neighborhood}
          </span>
          <h3 className="font-heading font-bold text-white text-xl leading-tight">{venue.name}</h3>
        </div>
      </div>

      <div className="p-8">
        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="bg-gray-50 rounded-xl p-3 text-center">
            <p className="font-heading font-bold text-secondary text-lg">{venue.eventTypes.length}</p>
            <p className="font-body text-xs text-gray-400">tipos de evento</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-3 text-center">
            <p className="font-heading font-bold text-secondary text-lg">{venue.features.length}</p>
            <p className="font-body text-xs text-gray-400">diferenciais</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-3 text-center">
            <p className="font-heading font-bold text-secondary text-lg">5.000+</p>
            <p className="font-body text-xs text-gray-400">fones STIB</p>
          </div>
        </div>

        <p className="font-body text-sm text-gray-500 leading-relaxed mb-6 line-clamp-3">
          {venue.description}
        </p>

        <div className="flex flex-wrap gap-2 mb-6">
          {venue.eventTypes.slice(0, 3).map((et, i) => (
            <span key={i} className="inline-flex items-center gap-1 bg-accent/10 text-accent-700 px-3 py-1 rounded-full font-body text-xs">
              <i className="ri-check-line text-accent-500 text-xs"></i>
              {et}
            </span>
          ))}
        </div>

        <Link
          to={`/locais/${venue.slug}`}
          className="flex items-center justify-center gap-2 w-full bg-primary text-white py-3.5 rounded-full font-body font-semibold text-sm hover:bg-opacity-90 transition-all hover:scale-[1.02] whitespace-nowrap cursor-pointer"
        >
          Ver palestra silenciosa no {venue.name.split(' ')[0]}
          <i className="ri-arrow-right-line text-white text-sm"></i>
        </Link>
      </div>
    </div>
  );
}

export default function VenuesHubPage() {
  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: introRef, isVisible: introVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation({ threshold: 0.05 });
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation({ threshold: 0.2 });

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://stibpalestrasilenciosasp.com.br';

  useEffect(() => {
    document.title = 'Palestra Silenciosa nos Principais Locais de Eventos de SP | STIB';

    const schemas = [
      {
        id: 'venues-hub-organization',
        data: {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          '@id': `${siteUrl}/#organization`,
          name: 'STIB - Palestra Silenciosa São Paulo',
          alternateName: 'STIB',
          url: siteUrl,
          description: 'Empresa especializada em aluguel de fones sem fio para palestra silenciosa nos principais locais de eventos de São Paulo.',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'São Paulo',
            addressRegion: 'SP',
            addressCountry: 'BR',
          },
        },
      },
      {
        id: 'venues-hub-breadcrumb',
        data: {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: 'Locais de Eventos', item: `${siteUrl}/locais` },
          ],
        },
      },
      {
        id: 'venues-hub-itemlist',
        data: {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: 'Locais de Eventos Parceiros STIB',
          description: 'Principais locais de eventos em São Paulo onde a STIB oferece palestra silenciosa com fones sem fio.',
          numberOfItems: ALL_VENUES.length,
          itemListElement: ALL_VENUES.map((venue, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            item: {
              '@type': 'LocalBusiness',
              name: venue.name,
              url: `${siteUrl}/locais/${venue.slug}`,
              address: {
                '@type': 'PostalAddress',
                addressLocality: 'São Paulo',
                addressRegion: 'SP',
                addressCountry: 'BR',
              },
              image: `${siteUrl}/locais/${venue.slug}`,
              description: venue.description,
            },
          })),
        },
      },
    ];

    schemas.forEach(({ id, data }) => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
      const s = document.createElement('script');
      s.id = id;
      s.type = 'application/ld+json';
      s.textContent = JSON.stringify(data);
      document.head.appendChild(s);
    });

    return () => {
      schemas.forEach(({ id }) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [siteUrl]);

  return (
    <>
      <Header />

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section
        ref={heroRef}
        className="relative min-h-[70vh] w-full flex items-center justify-center overflow-hidden mt-[72px]"
      >
        <div className="absolute inset-0">
          <img
            src="/img/stib/setor-arquitetura.webp"
            alt="Palestra Silenciosa nos Principais Locais de Eventos de São Paulo"
            className={`w-full h-full object-cover object-top transition-all duration-1000 ${heroVisible ? 'scale-100 opacity-100' : 'scale-105 opacity-0'}`}
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/50 to-black/80"></div>
        </div>

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto w-full">
          <div className={`inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white/90 text-xs font-semibold uppercase tracking-widest px-5 py-2 rounded-full mb-8 transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-building-2-line text-primary text-sm"></i>
            </div>
            parceiros de eventos
          </div>

          <header>
            <h1 className={`font-heading font-extrabold text-4xl md:text-6xl text-white mb-6 tracking-tight leading-tight lowercase transition-all duration-700 delay-100 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              palestra silenciosa nos melhores locais de são paulo
            </h1>
          </header>

          <p className={`font-body font-light text-lg md:text-xl text-white/80 mb-10 max-w-2xl mx-auto leading-relaxed transition-all duration-700 delay-200 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            A <strong className="text-white">STIB</strong> é fornecedora de palestra silenciosa nos principais centros de convenções e espaços de eventos de São Paulo. Tecnologia premium de fones sem fio onde seu evento acontece.
          </p>

          <div className={`flex flex-wrap items-center justify-center gap-6 transition-all duration-700 delay-300 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="text-center">
              <p className="font-heading font-bold text-primary text-3xl">{ALL_VENUES.length}</p>
              <p className="font-body text-white/60 text-sm">venues parceiros</p>
            </div>
            <div className="w-px h-10 bg-white/20"></div>
            <div className="text-center">
              <p className="font-heading font-bold text-primary text-3xl">5.000+</p>
              <p className="font-body text-white/60 text-sm">fones em estoque</p>
            </div>
            <div className="w-px h-10 bg-white/20"></div>
            <div className="text-center">
              <p className="font-heading font-bold text-primary text-3xl">500+</p>
              <p className="font-body text-white/60 text-sm">eventos realizados</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── INTRO ────────────────────────────────────────────── */}
      <section ref={introRef} className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className={`font-heading font-bold text-4xl md:text-5xl text-secondary lowercase mb-8 transition-all duration-700 ${introVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            por que os venues escolhem a stib?
          </h2>
          <p className={`font-body text-lg text-gray-600 leading-relaxed mb-6 transition-all duration-700 delay-100 ${introVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            Os principais espaços de eventos de São Paulo — desde centros de convenções até feiras de grande porte — demandam tecnologia de áudio que funcione em escala. A <strong className="text-secondary">STIB</strong> oferece fones sem fio RF com alcance de 500m, capacidade para 5.000 participantes e suporte técnico presencial.
          </p>
          <p className={`font-body text-lg text-gray-600 leading-relaxed transition-all duration-700 delay-200 ${introVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            Nossos clientes incluem organizadores de eventos nos mais prestigiados venues da cidade. Seja um congresso no <strong>Frei Caneca</strong>, uma feira no <strong>Expo Center Norte</strong> ou um evento corporativo no <strong>Transamerica</strong> — a STIB entrega qualidade comprovada.
          </p>
        </div>
      </section>

      {/* ── VENUE CARDS GRID ───────────────────────────────── */}
      <section ref={gridRef} className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
              Locais de Eventos em São Paulo
            </span>
            <h2 className={`font-heading font-bold text-4xl md:text-5xl text-secondary lowercase transition-all duration-700 ${gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              escolha o venue
            </h2>
            <p className={`font-body text-gray-500 mt-4 max-w-2xl mx-auto transition-all duration-700 delay-100 ${gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              Clique em um venue para ver como a palestra silenciosa da STIB é a solução ideal para eventos naquele espaço
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {ALL_VENUES.map((venue, index) => (
              <VenueCard key={venue.slug} venue={venue} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY WORK WITH STIB AT VENUES ─────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
              Vantagens Técnicas
            </span>
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-secondary lowercase">
              stib + venue = evento perfeito
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-signal-tower-line',
                title: 'Tecnologia RF Premium',
                description: 'Fones sem fio por rádio frequência funcionam perfeitamente mesmo em venues com milhares de dispositivos conectados. Sem interferência de WiFi ou Bluetooth.',
              },
              {
                icon: 'ri-group-line',
                title: 'Capacidade para 5.000+',
                description: 'Estoque de 5.000+ fones permite atender desde salas íntimas de 50 pessoas até grandes convenções nos maiores pavilhões de São Paulo.',
              },
              {
                icon: 'ri-customer-service-2-line',
                title: 'Suporte Presencial',
                description: 'Equipe técnica presente durante todo o evento no venue. Montagem rápida, operação impecável e resolução imediata de qualquer necessidade.',
              },
              {
                icon: 'ri-map-pin-line',
                title: 'Logística Otimizada',
                description: 'Conhecimento da infraestrutura de cada venue. Acesso de serviço, configuração elétrica, pontos de montagem — tudo mapeado para agilidade.',
              },
              {
                icon: 'ri-volume-mute-line',
                title: 'Zero Interferência',
                description: 'Salas adjacentes? Pavilhões múltiplos? Nossos canais RF independentes permitem dezenas de palestras simultâneas sem cruzamento de áudio.',
              },
              {
                icon: 'ri-global-line',
                title: 'Tradução Simultânea',
                description: 'Elimine cabines de tradução tradicionais. Os participantes selecionam o idioma direto no fone — ideal para eventos internacionais nos grandes venues.',
              },
            ].map((item, i) => (
              <div key={i} className="bg-gray-50 rounded-2xl p-8 border border-gray-100 hover:border-primary/20 transition-all duration-300 hover:-translate-y-1">
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-primary/10 mb-6">
                  <i className={`${item.icon} text-primary text-2xl`}></i>
                </div>
                <h3 className="font-heading font-bold text-xl text-secondary mb-3">{item.title}</h3>
                <p className="font-body text-sm text-gray-500 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ───────────────────────────────────────── */}
      <section ref={ctaRef} className="relative py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-secondary to-secondary/90"></div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className={`font-heading font-bold text-4xl md:text-5xl text-white lowercase mb-6 transition-all duration-700 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            seu evento em qualquer venue de sp
          </h2>
          <p className={`font-body text-lg text-white/80 mb-10 max-w-2xl mx-auto leading-relaxed transition-all duration-700 delay-100 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            Não encontrou o venue do seu evento? A STIB atende <strong>todos os principais espaços de eventos de São Paulo</strong> e região. Palestra silenciosa com tecnologia de ponta onde você precisar.
          </p>
          <div className={`flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-700 delay-200 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <a
              href="/orcamento"
              className="inline-flex items-center gap-3 bg-primary text-white px-10 py-4 rounded-full font-body font-bold text-base uppercase hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              SOLICITAR ORÇAMENTO
              <i className="ri-arrow-right-line text-white text-lg"></i>
            </a>
            <a
              href="https://wa.me/5511917776227?text=Olá! Vi a página de venues no site e gostaria de solicitar um orçamento para meu evento."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/30 text-white px-10 py-4 rounded-full font-body font-semibold text-base hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-green-400 text-xl"></i>
              WhatsApp
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}